/**
 * SiteQrDialog — admin/supervisor view of a job's Site QR poster
 * (Jun 2026). Generate, view, print, and rotate the QR code that gets
 * posted at the physical job site. Workers scan it to verify they're
 * on site at clock-in and when returning from breaks/material runs.
 *
 * Backend: GET/POST /api/jobs/{id}/site-qr  (routes/site_qr.py)
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { QrCode, Printer, RotateCw, Loader2, Trash2, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.__BACKEND_URL__}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const esc = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export default function SiteQrDialog({ job, open, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [newLocked, setNewLocked] = useState(false);
  const [lockSaving, setLockSaving] = useState(false);

  const toggleLock = async (value) => {
    setLockSaving(true);
    try {
      const r = await axios.put(`${API}/jobs/${job.id}/site-qr/settings`, { location_locked: value }, { headers: authHeaders() });
      setData(r.data);
      toast.success(value
        ? 'Location-locked: workers can now only scan this QR while at the job site.'
        : 'Location lock removed — the QR can be scanned from anywhere.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update setting');
    } finally {
      setLockSaving(false);
    }
  };

  const load = useCallback(async () => {
    if (!job?.id) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/jobs/${job.id}/site-qr`, { headers: authHeaders() });
      setData(r.data);
    } catch (err) {
      if (err.response?.status === 404) setData(null);
      else toast.error(err.response?.data?.detail || 'Could not load site QR');
    } finally {
      setLoading(false);
    }
  }, [job?.id]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const generate = async (rotate = false) => {
    setBusy(true);
    try {
      const locked = rotate ? !!data?.location_locked : newLocked;
      const r = await axios.post(`${API}/jobs/${job.id}/site-qr`, { location_locked: locked }, { headers: authHeaders() });
      setData(r.data);
      toast.success(rotate
        ? 'New QR code generated — old posters are now invalid. Print and replace them.'
        : 'Site QR code generated. Print it and post it at the job site.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not generate site QR');
    } finally {
      setBusy(false);
    }
  };

  const removeQr = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/jobs/${job.id}/site-qr`, { headers: authHeaders() });
      setData(null);
      setConfirmRemove(false);
      toast.success('QR clock-in turned off for this job — workers will no longer be asked to scan.');
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove site QR');
    } finally {
      setBusy(false);
    }
  };

  const handlePrint = () => {
    if (!data?.qr_png) return;
    const address = job?.job_address || job?.street_address || '';
    const created = data.created_at ? new Date(data.created_at).toLocaleDateString() : '';
    const w = window.open('', '_blank', 'width=700,height=900');
    if (!w) {
      toast.error('Pop-up blocked — allow pop-ups to print the QR poster.');
      return;
    }
    w.document.write(`<!DOCTYPE html><html><head><title>Site QR — ${esc(job?.title)}</title>
<style>
  body { font-family: Arial, Helvetica, sans-serif; text-align: center; padding: 36px 24px; color: #111; }
  .brand { font-size: 14px; letter-spacing: 3px; text-transform: uppercase; color: #555; margin-bottom: 6px; }
  h1 { font-size: 26px; margin: 0 0 4px; }
  .addr { font-size: 15px; color: #444; margin: 0 0 20px; }
  img { width: 340px; height: 340px; border: 4px solid #111; border-radius: 12px; padding: 10px; }
  .instructions { max-width: 440px; margin: 22px auto 0; font-size: 16px; line-height: 1.5; }
  .steps { text-align: left; display: inline-block; margin-top: 10px; font-size: 15px; line-height: 1.7; }
  .foot { margin-top: 26px; font-size: 11px; color: #888; }
</style></head><body>
  <div class="brand">Hines Time · Site Clock-In Code</div>
  <h1>${job?.job_number ? esc(job.job_number) + ' — ' : ''}${esc(job?.title)}</h1>
  ${address ? `<p class="addr">${esc(address)}</p>` : ''}
  <img src="${data.qr_png}" alt="Site QR code" />
  <div class="instructions">
    <strong>Workers: scan with your phone camera or the Hines Time app</strong>
    <div class="steps">
      1. When you arrive — scan to clock in.<br/>
      2. When you leave — scan the same code to clock out.
    </div>
  </div>
  <p class="foot">Generated ${esc(created)}. If a new code is generated for this job, this poster becomes invalid.</p>
  <script>window.onload = function () { window.print(); };</script>
</body></html>`);
    w.document.close();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-[95vw] sm:max-w-md" data-testid="site-qr-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="w-5 h-5 text-[#1E88E5]" />
            <span>Site QR Code</span>
          </DialogTitle>
          <DialogDescription>
            Print this code and post it at the job site. Workers scan it to verify they're
            physically on site when clocking in and when returning from breaks or material runs.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-400" data-testid="site-qr-loading">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : data ? (
          <div className="space-y-4">
            <div className="flex justify-center">
              <img
                src={data.qr_png}
                alt="Site QR code"
                className="w-56 h-56 rounded-lg bg-white p-2"
                data-testid="site-qr-image"
              />
            </div>
            <p className="text-center text-xs" style={{ color: '#64748B' }}>
              {job?.job_number ? `${job.job_number} · ` : ''}{job?.title}
              {data.created_at ? ` · generated ${new Date(data.created_at).toLocaleDateString()}` : ''}
            </p>

            {/* Location-locked scanning */}
            <div className="flex items-start justify-between gap-3 rounded-lg p-3" style={{ border: '1px solid rgba(30,136,229,0.3)', background: 'rgba(30,136,229,0.05)' }}>
              <div className="min-w-0">
                <p className="text-sm font-medium text-white flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5" style={{ color: '#7BB8E8' }} />
                  Location-locked scanning
                </p>
                <p className="text-[11px] mt-0.5" style={{ color: '#94A3B8' }}>
                  {data.location_locked
                    ? <>Workers can only scan within <b>{data.geofence_radius_m} m</b> of the job site (GPS required).</>
                    : 'When on, the QR only works while the worker is physically at the job site (GPS required).'}
                  {!data.has_job_location && (
                    <span style={{ color: '#FFA000' }}> This job has no GPS location yet — set the job address first.</span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2 pt-1 flex-shrink-0">
                {lockSaving && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
                <Switch
                  checked={!!data.location_locked}
                  onCheckedChange={toggleLock}
                  disabled={lockSaving || (!data.location_locked && !data.has_job_location)}
                  data-testid="site-qr-location-lock-switch"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handlePrint}
                className="flex-1 bg-[#1E88E5] hover:bg-[#1976D2] text-white"
                data-testid="site-qr-print-btn"
              >
                <Printer className="w-4 h-4 mr-2" /> Print poster
              </Button>
              <Button
                variant="outline"
                onClick={() => generate(true)}
                disabled={busy}
                title="Generate a new code — invalidates any previously printed posters"
                data-testid="site-qr-rotate-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCw className="w-4 h-4 mr-2" />}
                Regenerate
              </Button>
            </div>
            <p className="text-[11px] text-center" style={{ color: '#64748B' }}>
              Regenerating invalidates old posters — reprint and replace them at the site.
            </p>
            <div className="rounded-lg p-3" style={{ border: '1px solid rgba(239,68,68,0.35)', background: 'rgba(239,68,68,0.05)' }}>
              {confirmRemove ? (
                <div className="space-y-2">
                  <p className="text-xs" style={{ color: '#FCA5A5' }}>
                    Turn off QR clock-in for this job? Workers will no longer be asked to scan.
                    You can re-add a QR any time with the Generate button.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={removeQr}
                      disabled={busy}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                      data-testid="site-qr-remove-confirm-btn"
                    >
                      {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1.5" />}
                      Yes, remove QR
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setConfirmRemove(false)} data-testid="site-qr-remove-cancel-btn">
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmRemove(true)}
                  className="w-full text-xs font-medium flex items-center justify-center gap-1.5 py-1"
                  style={{ color: '#F87171' }}
                  data-testid="site-qr-remove-btn"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Remove QR requirement from this job
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-4 text-center" data-testid="site-qr-empty">
            <QrCode className="w-12 h-12 mx-auto" style={{ color: '#2D3748' }} />
            <p className="text-sm" style={{ color: '#94A3B8' }}>
              No QR code has been generated for this job yet.
            </p>
            <label
              className="flex items-start gap-3 rounded-lg p-3 text-left cursor-pointer"
              style={{ border: '1px solid rgba(30,136,229,0.3)', background: 'rgba(30,136,229,0.05)' }}
            >
              <div className="pt-0.5">
                <Switch
                  checked={newLocked}
                  onCheckedChange={setNewLocked}
                  data-testid="site-qr-new-location-lock-switch"
                />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-white flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5" style={{ color: '#7BB8E8' }} />
                  Location-locked scanning
                </p>
                <p className="text-[11px] mt-0.5" style={{ color: '#94A3B8' }}>
                  Workers can only scan this QR while physically at the job site (GPS check
                  against the job's geofence). Requires the job to have an address/location.
                </p>
              </div>
            </label>
            <Button
              onClick={() => generate(false)}
              disabled={busy}
              className="bg-[#1E88E5] hover:bg-[#1976D2] text-white"
              data-testid="site-qr-generate-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <QrCode className="w-4 h-4 mr-2" />}
              Generate Site QR Code
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
